/* ============================================================================
 * AccountStatusBanner — top-of-dashboard notice when the account isn't simply
 * active: cancelled (paid time still running), read-only, or a crew member of
 * an account that has ended. Renders nothing for a normal active account.
 * The database enforces read-only (migration 110); this only explains it.
 * ========================================================================== */

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Info, Lock } from 'lucide-react';
import { isCancelling, useAccountState } from '@/lib/accountData';

const fmt = (iso, lang) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US', {
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

const AccountStatusBanner = () => {
  const { t, i18n } = useTranslation();
  const { state } = useAccountState();
  if (!state || (state.status === 'active' && !isCancelling(state))) return null;

  const lang = i18n.language;
  let text;
  let strong = true;
  if (!state.is_owner) {
    text = t('account.banner_closed_member');
  } else if (isCancelling(state)) {
    strong = false;
    text = t('account.banner_cancelling', { until: fmt(state.access_until, lang) });
  } else if (state.status === 'read_only') {
    text = t('account.banner_read_only', { date: fmt(state.delete_after, lang) });
  } else if (state.status === 'deleted') {
    text = t('account.banner_deleted_kept', { date: fmt(state.pq_profile_expires_at, lang) });
  } else {
    text = t('account.banner_deleting');
  }

  const Icon = strong ? Lock : Info;
  return (
    <div
      role="status"
      className={
        'mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm ' +
        (strong
          ? 'border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground'
          : 'border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 text-foreground')
      }
      data-testid="account-status-banner"
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1">{text}</span>
      {state.is_owner ? (
        <Link
          to="/dashboard/settings?tab=account"
          className="text-xs font-medium underline underline-offset-2 whitespace-nowrap"
        >
          {t('account.banner_link')}
        </Link>
      ) : null}
    </div>
  );
};

export default AccountStatusBanner;
